import { useEffect, useReducer, useState } from 'react';
import { Modal, Form, CloseButton, Badge, Row, Col } from 'react-bootstrap';
import { TextButton } from 'Components/Button';
import { PageInfo } from 'Components/PageInformation/PageInformation';
import { displayErrors } from 'Components/Messages/ErrorMessage';
import { subscribeEmail, unsubscribeEmail } from './SubscribeAxiosFuncs';

// ─── Reducer ────────────────────────────────────────────────────────────────

const ACTIONS = {
    EMAIL_CHANGE: 'EMAIL_CHANGE',
    EMAIL_SET_ERRORS: 'EMAIL_SET_ERRORS',
    EMAIL_REMOVE_ERRORS: 'EMAIL_REMOVE_ERRORS',
    REASONS_TOGGLE: 'REASONS_TOGGLE',
    CALL_API: 'CALL_API',
    SUCCESS: 'SUCCESS',
    GLOBAL_ERROR: 'GLOBAL_ERROR',
    RESET: 'RESET',
};

const createInitialState = () => ({
    email: { value: '', validation: { isInvalid: false, errors: [] } },
    reasons: [],
    loading: false,
    view: 'form',
    globalError: null,
    successMessage: null,
    emailSent: false,
});

const formReducer = (state, action) => {
    switch (action.type) {
        case ACTIONS.EMAIL_CHANGE:
            return {
                ...state,
                globalError: null,
                email: { value: action.value, validation: { isInvalid: false, errors: [] } },
            };
        case ACTIONS.EMAIL_SET_ERRORS:
            return {
                ...state,
                loading: false,
                email: { ...state.email, validation: { isInvalid: true, errors: action.errors } },
            };
        case ACTIONS.EMAIL_REMOVE_ERRORS:
            return { ...state, email: { ...state.email, validation: { isInvalid: false, errors: [] } } };
        case ACTIONS.REASONS_TOGGLE: {
            const reasons = state.reasons.includes(action.reason)
                ? state.reasons.filter(r => r !== action.reason)
                : [...state.reasons, action.reason];
            return { ...state, reasons };
        }
        case ACTIONS.CALL_API:
            return { ...state, loading: true, globalError: null };
        case ACTIONS.SUCCESS:
            return { ...state, loading: false, view: 'success', successMessage: action.message, emailSent: action.emailSent ?? false };
        case ACTIONS.GLOBAL_ERROR:
            return { ...state, loading: false, globalError: action.message };
        case ACTIONS.RESET:
            return createInitialState();
        default:
            return state;
    }
};

// ─── Constants ───────────────────────────────────────────────────────────────

const UNSUBSCRIBE_REASONS = [
    "I no longer want to receive these emails",
    "I got too many emails",
    "The emails aren't relevant",
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function applyApiFieldErrors(res, dispatch) {
    const emailErrors = res?.data?.fieldErrors?.emailAddress?.errorMessages;
    if (emailErrors?.length > 0) {
        dispatch({ type: ACTIONS.EMAIL_SET_ERRORS, errors: emailErrors });
        return true;
    }
    return false;
}

function getGlobalErrorMsg(res, err) {
    return (
        err?.globalErrorMessageList?.[0] ??
        err?.data?.globalErrorMessageList?.[0] ??
        res?.data?.globalErrorMessageList?.[0] ??
        'Something went wrong. Please try again.'
    );
}

// ─── Shared email field ───────────────────────────────────────────────────────

function EmailField({ state, dispatch, id }) {
    return (
        <Form.Group controlId={id}>
            <Form.Label className="fw-semibold">Email</Form.Label>
            <Form.Control
                type="text"
                placeholder="your.email@example.com"
                value={state.email.value}
                onChange={e => dispatch({ type: ACTIONS.EMAIL_CHANGE, value: e.target.value })}
                isInvalid={state.email.validation.isInvalid}
            />
            <Form.Control.Feedback type="invalid">
                {displayErrors(id, state.email.validation.errors)}
            </Form.Control.Feedback>
        </Form.Group>
    );
}

// ─── Subscribe tab body ───────────────────────────────────────────────────────

function SubscribeBody({ state, dispatch }) {
    return (
        <div className="d-flex flex-column gap-3">
            <h2 id="subscribe-modal-title" className="subscribe-modal-title mb-0">Subscribe</h2>
            <p className="mb-0">
                Thank you for your interest in subscribing to email notifications from the PSO
                Privacy Protection Center (PSOPPC). Please provide your information below. You
                may unsubscribe at any time.
            </p>
            {state.globalError && <PageInfo type="error" message={state.globalError} />}
            <hr className="my-0" />
            <Form onSubmit={e => e.preventDefault()}>
                <EmailField state={state} dispatch={dispatch} id="subscribe-email" />
            </Form>
        </div>
    );
}

// ─── Unsubscribe tab body ─────────────────────────────────────────────────────

function UnsubscribeBody({ state, dispatch }) {
    return (
        <div className="d-flex flex-column gap-3">
            <h2 id="subscribe-modal-title" className="subscribe-modal-title mt-sm-0 mb-0">Unsubscribe</h2>
            <p className="mb-0">
                We are sad to see you go! Please complete the form below to unsubscribe from
                PSOPPC. You may also contact us directly at{' '}
                <a href="mailto:support@psoppc.org">support@psoppc.org</a>.
            </p>
            {state.globalError && <PageInfo type="error" message={state.globalError} />}
            <hr className="my-0" />
            <Form onSubmit={e => e.preventDefault()}>
                <Row className="g-3">
                    <Col xs={12} md={6}>
                        <EmailField state={state} dispatch={dispatch} id="unsubscribe-email" />
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Group role="group" aria-label="Reason">
                            <p className="fw-semibold mb-1">
                                <span className="me-2 form-label-email-unsubscribe-reason">Reason</span>
                                <Badge bg="optional">Optional</Badge>
                            </p>
                            {UNSUBSCRIBE_REASONS.map(reason => (
                                <Form.Check
                                    key={reason}
                                    type="checkbox"
                                    id={`reason-${reason.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                                    label={reason}
                                    checked={state.reasons.includes(reason)}
                                    onChange={() => dispatch({ type: ACTIONS.REASONS_TOGGLE, reason })}
                                />
                            ))}
                        </Form.Group>
                    </Col>
                </Row>
            </Form>
        </div>
    );
}

// ─── Success body ─────────────────────────────────────────────────────────────

function SuccessBody({ isSubscribe, emailSent, message }) {
    return (
        <PageInfo
            type={emailSent && !isSubscribe ? 'warning' : 'info'}
            combo
            title={emailSent ? 'We sent you an email' : 'Info'}
            message={message}
        />
    );
}

// ─── Main modal ───────────────────────────────────────────────────────────────

function SubscribeModal({ show, handleClose, initialTab = 'subscribe' }) {
    const [activeTab, setActiveTab] = useState(initialTab);

    useEffect(() => {
        if (show) setActiveTab(initialTab);
    }, [show, initialTab]);
    const [subState, subDispatch] = useReducer(formReducer, null, createInitialState);
    const [unsubState, unsubDispatch] = useReducer(formReducer, null, createInitialState);

    const handleModalClose = () => {
        subDispatch({ type: ACTIONS.RESET });
        unsubDispatch({ type: ACTIONS.RESET });
        setActiveTab(initialTab);
        handleClose();
    };

    const handleSubscribeSubmit = async () => {
        subDispatch({ type: ACTIONS.EMAIL_REMOVE_ERRORS });
        subDispatch({ type: ACTIONS.CALL_API });
        try {
            const res = await subscribeEmail(subState.email.value);
            if (res?.data?.status < 0) {
                if (!applyApiFieldErrors(res, subDispatch))
                    subDispatch({ type: ACTIONS.GLOBAL_ERROR, message: getGlobalErrorMsg(res, null) });
                return;
            }
            subDispatch({ type: ACTIONS.SUCCESS, message: res.data.message, emailSent: res.data.data?.emailSent ?? false });
        } catch (err) {
            if (!applyApiFieldErrors({ data: err?.data }, subDispatch))
                subDispatch({ type: ACTIONS.GLOBAL_ERROR, message: getGlobalErrorMsg(null, err) });
        }
    };

    const handleUnsubscribeSubmit = async () => {
        unsubDispatch({ type: ACTIONS.EMAIL_REMOVE_ERRORS });
        const unSubscribeReasons = unsubState.reasons.length > 0
            ? unsubState.reasons.join(', ')
            : null;
        unsubDispatch({ type: ACTIONS.CALL_API });
        try {
            const res = await unsubscribeEmail(unsubState.email.value, unSubscribeReasons);
            if (res?.data?.status < 0) {
                if (!applyApiFieldErrors(res, unsubDispatch))
                    unsubDispatch({ type: ACTIONS.GLOBAL_ERROR, message: getGlobalErrorMsg(res, null) });
                return;
            }
            unsubDispatch({ type: ACTIONS.SUCCESS, message: res.data.message, emailSent: res.data.data?.emailSent ?? false });
        } catch (err) {
            if (!applyApiFieldErrors({ data: err?.data }, unsubDispatch))
                unsubDispatch({ type: ACTIONS.GLOBAL_ERROR, message: getGlobalErrorMsg(null, err) });
        }
    };

    const isSubSuccess = subState.view === 'success';
    const isUnsubSuccess = unsubState.view === 'success';
    const isActiveSuccess = activeTab === 'subscribe' ? isSubSuccess : isUnsubSuccess;
    const isActiveLoading = activeTab === 'subscribe' ? subState.loading : unsubState.loading;
    const successTitle = (activeTab === 'subscribe' ? subState.emailSent : unsubState.emailSent)
        ? 'We sent you an email'
        : 'Info';
    const modalTitle = isActiveSuccess
        ? successTitle
        : (activeTab === 'subscribe' ? 'Subscribe' : 'Unsubscribe');

    return (
        <Modal
            show={show}
            onHide={handleModalClose}
            centered
            size="lg"
            dialogClassName="subscribe-modal-dialog"
            aria-label={modalTitle}
        >
            {isActiveSuccess ? (
                <Modal.Header className="border-bottom-0">
                    <CloseButton aria-label="Close" onClick={handleModalClose} />
                </Modal.Header>
            ) : (
                <Modal.Header className="border-bottom-0 flex-column px-4 pt-3 pb-0 gap-2">
                    <div className="d-flex justify-content-end w-100">
                        <CloseButton aria-label="Close" onClick={handleModalClose} />
                    </div>
                    <div className="d-flex w-100 gap-1 subscribe-tab-container">
                        <div
                            role="button"
                            tabIndex={0}
                            className={`align-content-center subscribe-tab${activeTab === 'subscribe' ? ' active' : ''}`}
                            onClick={() => setActiveTab('subscribe')}
                            onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setActiveTab('subscribe')}
                        >
                            Subscribe to PSOPPC
                        </div>
                        <div
                            role="button"
                            tabIndex={0}
                            className={`subscribe-tab${activeTab === 'unsubscribe' ? ' active' : ''}`}
                            onClick={() => setActiveTab('unsubscribe')}
                            onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setActiveTab('unsubscribe')}
                        >
                            Unsubscribe from PSOPPC
                        </div>
                    </div>
                </Modal.Header>
            )}

            <Modal.Body className={isActiveSuccess ? 'p-0' : 'px-4'}>
                {isActiveSuccess ? (
                    <SuccessBody
                        isSubscribe={activeTab === 'subscribe'}
                        emailSent={activeTab === 'subscribe' ? subState.emailSent : unsubState.emailSent}
                        message={activeTab === 'subscribe' ? subState.successMessage : unsubState.successMessage}
                    />
                ) : (
                    <>
                        <div className={activeTab !== 'subscribe' ? 'd-none' : ''}>
                            <SubscribeBody state={subState} dispatch={subDispatch} />
                        </div>
                        <div className={activeTab !== 'unsubscribe' ? 'd-none' : ''}>
                            <UnsubscribeBody state={unsubState} dispatch={unsubDispatch} />
                        </div>
                    </>
                )}
            </Modal.Body>

            <Modal.Footer className={isActiveSuccess ? '' : 'border-top-0 px-4 pb-4'}>
                {isActiveSuccess ? (
                    <TextButton
                        variant="normal"
                        content="OK"
                        className={"subscribe-info-modal-okay-button"}
                        onClick={handleModalClose}
                    />
                ) : (
                    <>
                        <TextButton
                            variant="normal"
                            content={isActiveLoading ? 'Submitting' : 'Submit'}
                            disabled={isActiveLoading}
                            onClick={activeTab === 'subscribe' ? handleSubscribeSubmit : handleUnsubscribeSubmit}
                        />
                        <TextButton
                          variant="outline"
                          content="Cancel"
                          onClick={handleModalClose}
                        />
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
}

export default SubscribeModal;
